import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./Users.css";
import "bootstrap/dist/css/bootstrap.min.css";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);

  // pagination dari server
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referral, setReferral] = useState("");
  const [accountType, setAccountType] = useState("real");
  const [status, setStatus] = useState("active");
  const [cryptoList, setCryptoList] = useState([]);
  const [cryptoBank, setCryptoBank] = useState("");
  const [walletCode, setWalletCode] = useState("");

  // === FETCH USERS DENGAN PAGINATION SERVER ===
  const fetchUsers = async (page = 1) => {
    try {
      const res = await fetch(
        `https://api.backofficemain.com/get_users.php?page=${page}`
      );
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
        setCurrentPage(data.page || page);
        setTotalPages(data.total_pages || 1);
        setTotalUsers(data.total || 0);
      } else {
        setMessage("❌ Gagal memuat users: " + (data.message || ""));
      }
    } catch (err) {
      console.error(err);
      setMessage("⚠️ Server error saat memuat users.");
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, []);

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setFirstname("");
    setLastname("");
    setEmail("");
    setPhone("");
    setReferral("");
    setAccountType("real");
    setStatus("active");
    setCryptoBank("");
    setWalletCode("");
    setEditMode(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);
    formData.append("firstname", firstname);
    formData.append("lastname", lastname);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("referral", referral);
    formData.append("staff_code", referral);
    formData.append("account_type", accountType);
    formData.append("status", status);
    formData.append("crypto_bank", cryptoBank);
    formData.append("wallet_code", walletCode);

    const url = editMode
      ? "https://api.backofficemain.com/update_user.php"
      : "https://api.backofficemain.com/add_user.php";

    try {
      const res = await fetch(url, { method: "POST", body: formData });
      const data = await res.json();

      if (data.success) {
        setMessage(editMode ? "✅ User updated successfully!" : "✅ User added!");
        fetchUsers(currentPage); // reload page aktif
        setShowModal(false);
        resetForm();
      } else {
        setMessage("❌ " + data.message);
      }
    } catch (error) {
      console.error(error);
      setMessage("⚠️ Server error.");
    }
  };

  // === LOAD LIST CRYPTO BANK ===
  useEffect(() => {
    fetchCryptoList();
  }, []);

  const fetchCryptoList = async () => {
    try {
      const res = await fetch("https://api.backofficemain.com/get_crypto_bank.php");
      const data = await res.json();
      if (data.success) setCryptoList(data.data);
    } catch (err) {
      console.error("Failed to load crypto list:", err);
    }
  };

  // === EDIT USER ===
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setShowModal(true);
    setUsername(user.username);
    setPassword("");
    setFirstname(user.firstname || "");
    setLastname(user.lastname || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setReferral(user.referral || "");
    setAccountType(user.account_type || "real");
    setStatus(user.status || "active");

    // ---> BAGIAN PENTING <---
    // crypto_bank dari API bisa number / null
    // kita paksa jadi string supaya match dengan <option value="id">
    if (user.crypto_bank !== null && user.crypto_bank !== undefined) {
      setCryptoBank(String(user.crypto_bank));
    } else {
      setCryptoBank("");
    }

    setWalletCode(user.wallet_code || "");
  };

  const handleStatusToggle = async (username, currentStatus) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    try {
      const res = await fetch("https://api.backofficemain.com/update_status.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, status: newStatus }),
      });

      const data = await res.json();

      if (data.success) {
        setMessage(
          `✅ User ${newStatus === "active" ? "activated" : "suspended"}!`
        );
        fetchUsers(currentPage);
      } else {
        setMessage("❌ Failed to update user status: " + data.message);
      }
    } catch (err) {
      console.error(err);
      setMessage("⚠️ Server error while updating status.");
    } finally {
      setDropdownOpen(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".three-dots-btn") &&
        !event.target.closest(".user-action-dropdown")
      ) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // filter search (di dalam page yang lagi ditampilkan)
  const filteredUsers = users.filter(
    (u) =>
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchUsers(page);
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h3 className="fw-bold text-primary mb-2">User Management</h3>
        <div className="d-flex gap-2 align-items-center">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search users..."
            style={{ width: "220px" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            + Add User
          </button>
        </div>
      </div>

      {message && (
        <div className="alert alert-info py-2 text-center" role="alert">
          {message}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-start ps-4">Username</th>
                <th>Account</th>
                <th>Balance</th>
                <th>Phone</th>
                <th>Staff Code</th>
                <th>Status</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr key={index}>
                    <td className="ps-4">{user.username}</td>
                    <td>{user.account_type}</td>
                    <td>${user.balance}</td>
                    <td>{user.phone}</td>
                    <td>{user.staff_code}</td>
                    <td>
                      <span
                        className={`badge ${
                          user.status === "active"
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="text-end pe-4 position-relative">
                      <button
                        className="btn btn-light btn-sm border three-dots-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropdownOpen({
                            index,
                            x: rect.right - 180,
                            y: rect.bottom + window.scrollY,
                          });
                        }}
                      >
                        <i className="fas fa-ellipsis-v"></i>
                      </button>

                      {dropdownOpen?.index === index &&
                        ReactDOM.createPortal(
                          <div
                            className="user-action-dropdown card shadow-sm p-2"
                            style={{
                              position: "absolute",
                              top: dropdownOpen.y,
                              left: dropdownOpen.x,
                              zIndex: 1050,
                              minWidth: "160px",
                            }}
                          >
                            <button
                              className="dropdown-item text-start"
                              onClick={() =>
                                (window.location.href = `/view-user/${user.username}`)
                              }
                            >
                              <i className="fas fa-eye me-2"></i> View Details
                            </button>
                            <button
                              className="dropdown-item text-start"
                              onClick={() => handleEditUser(user)}
                            >
                              <i className="fas fa-edit me-2"></i> Edit
                            </button>
                            <button
                              className={`dropdown-item text-start ${
                                user.status === "active"
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                              onClick={() =>
                                handleStatusToggle(user.username, user.status)
                              }
                            >
                              <i
                                className={`fas ${
                                  user.status === "active"
                                    ? "fa-user-slash"
                                    : "fa-user-check"
                                } me-2`}
                              ></i>
                              {user.status === "active"
                                ? "Suspend"
                                : "Activate"}
                            </button>
                          </div>,
                          document.body
                        )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-3">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info & Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
        <div className="small text-muted mb-2">Total users: {totalUsers}</div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mb-2">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  «
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  »
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box p-4 rounded shadow">
            <h5 className="fw-bold mb-3 text-primary">
              {editMode ? "Edit User" : "Add User"}
            </h5>
            <form onSubmit={handleSaveUser}>
              <div className="row g-2">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={editMode}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="password"
                    className="form-control form-control-sm"
                    placeholder={
                      editMode
                        ? "Leave blank to keep password"
                        : "Password"
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={!editMode}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="First Name"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Last Name"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                {!editMode && (
                  <div className="col-12">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Referral Code"
                      value={referral}
                      onChange={(e) => setReferral(e.target.value)}
                    />
                  </div>
                )}
                <div className="col-md-6">
                  <select
                    className="form-select form-select-sm"
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <option value="real">Real</option>
                    <option value="fake">Fake</option>
                  </select>
                </div>

                {/* Crypto Bank */}
                <div className="col-md-6">
                  <select
                    className="form-select form-select-sm"
                    value={cryptoBank}
                    onChange={(e) => setCryptoBank(e.target.value)}
                    required
                  >
                    <option value="">Select Crypto Bank</option>
                    {cryptoList.map((crypto) => (
                      <option
                        key={crypto.id}
                        value={String(crypto.id)} // pastikan string
                      >
                        {crypto.crypto_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Wallet Code */}
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Wallet Code"
                    value={walletCode}
                    onChange={(e) => setWalletCode(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mt-3 text-end">
                <button
                  type="submit"
                  className="btn btn-success btn-sm me-2 px-3"
                >
                  {editMode ? "Update" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-3"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
